import baseApi from './base.api'
import { toast } from '@/common/util'
import type {
  SectionBasicType,
  OrderSectionsType,
  SecoesAtivasResponseType,
  VisibleSection,
  ActivesItemsSectionsType,
  FiltersParamsType,
  VisibleSectionItem,
  SecaoActivesItemsResponse
} from '@/types/types'

import { midiaService } from './midias.service'

type TopLancamentosItemResponse = {
  id: string
  ordem: number
  secaoId: string | null
  midiaId: string | null
  cursoId: string | null
  formacaoId: string | null
  active: boolean
}

const api = {
  ...baseApi,
  resource: 'Secao',

  // Map to store active AbortControllers for each idSection
  activeSearchRequests: new Map<string, AbortController>(),

  prepareParams(param) {
    const fPar = {
      exibirNoAprendizado: param.inLandingPage,
      tiposConteudos: param.content,
      tipos: param?.types || undefined,
      categoriasIds: param.categories,
      competenciasIds: param.competences,
      secoesIds: param.sections || param.secoesIds,
      obrigatorio: param.required,
      search: param.search,
      status: param.status,
      origem: param.origin,
      page: param.page,
      size: param.size
    }

    Object.keys(fPar).forEach(key => {
      if (typeof fPar[key] !== 'boolean' && !fPar[key]) delete fPar[key]
    })

    return fPar
  },

  async getAtivos(params?: Object) {
    try {
      return api.get<SecoesAtivasResponseType[]>('ativas', { ...params })
    } catch (error) {
      // const { notifications }: { notifications: string[] } = error.response?.data || {}
      // toast.error({message: notifications[0] || 'Não foi possível recuperar as seções'})
    }
  },

  async fileUpload(id: string, file) {
    try {
      const formData = new FormData()

      formData.append('arquivo', file)
      formData.append('secaoId', id)

      return await api.post(`${id}/fileUpload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar realizar upload do arquivo!' })
    }
  },

  async sendOrderSections(data: OrderSectionsType) {
    return await api.post(`Ordenar`, data)
  },

  async getListBasic(params?: SectionBasicType): Promise<{ success: boolean, data: { id: string, nome: string }[] }> {
    const filter = { ExibirEm: null }

    if (params?.type) {
      if (params.type === 'files') filter.ExibirEm = 'Midia'
      if (params.type === 'courses') filter.ExibirEm = 'Curso'
      if (params.type === 'trails') filter.ExibirEm = 'Formacao'
      if (params.type === 'assessments') filter.ExibirEm = 'Avaliacao'

      delete params.type
    }

    try {
      return await api.get(`Basic`, { ...params, ...filter })
    } catch (error) {
      // const { notifications }: { notifications: string[] } = error.response?.data || {}
      // toast.error({message: notifications[0] || 'Erro ao buscar lista de seções!'})
    }
  },

  async findActive(params: Record<string, any>, options?: any, contentOnly?: boolean) {
    if (contentOnly) {
      if (!params) params = {}
      params.types = ['Conteudo']
    }

    const reqParams = api.prepareParams(params)

    // Merge options with the abort signal
    const requestOptions = {
      ...options
    }

    return api
      .get<SecoesAtivasResponseType[]>('ActivesPaged', reqParams, requestOptions)
      .then(res => {
        return {
          success: true,
          page: res?.pageNumber,
          size: res?.pageSize,
          items: res?.totalItems,
          totalPages: res?.totalPages,
          data: res.data?.map(sec => {
            const key = String(sec.tipo ?? '').toLocaleLowerCase()

            const tp = ({
              comunicacao: 'communication',
              conteudo: 'content',
              top10: 'top10',
              lancamentos: 'releases',
              jornadas: 'journeys'
            } as const)[key]

            const result = {
              id: sec.id,
              name: sec.nome,
              observation: sec.observacao,
              showObservation: sec.exibirDescricao,
              type: tp,
              startDate: sec.dtPublicacaoInicial ? new Date(sec.dtPublicacaoInicial) : null,
              endDate: sec.dtPublicacaoFinal ? new Date(sec.dtPublicacaoFinal) : null,
              orderHome: sec.ordemAprendizado || 0,
              orderCatalog: sec.ordemCatalogo || 0,
              showInHome: sec.exibirNoAprendizado || false,
              imageUrl: sec.imagemDoCard || '',
              totalItems: sec.totalDeItens || 0,
              itemOrderingCriteria: sec.criterioDaOrdenacaoDosItens
            } as VisibleSection

            if (['content', 'top10', 'releases'].includes(tp)) {
              result.cardOrientation = sec.orientacaoDosCards === 'Horizontal' ? 'horizontal' : 'vertical'
              result.cardImage = sec.imagemDoCard
              result.backgroundImage = sec.imagemDoBanner
            } else {
              result.imageUrl = sec.urlDaImagem
              result.link = sec.link
              result.linkNewTab = sec.linkDeRedirecionamentoNewTab
            }

            return result
          })
        }
      })
      .finally(() => {
      })
  },

  findSectionItems(idSection: string, params: FiltersParamsType, options?: any) {
    // Cancel any previous request for this idSection
    const previousController = this.activeSearchRequests.get(idSection)
    if (previousController) {
      previousController.abort()
      this.activeSearchRequests.delete(idSection)
    }

    // Create new AbortController for this request
    const controller = new AbortController()
    this.activeSearchRequests.set(idSection, controller)

    const dataParams = this.prepareParams(params)

    if (dataParams?.tipoAmbiente) delete dataParams.tipoAmbiente

    Object.keys(dataParams).forEach(key => {
      if (typeof dataParams[key] !== 'boolean' && !dataParams[key]) delete dataParams[key]
    })

    // Temos 5 urls distintas... que tem o mesmo tipo de retorno...
    let eUrl = `${idSection}/ActivesItemsPaged`
    if (idSection === 'continue') eUrl = 'KeepLearningPaged'
    else if (idSection === 'suggested') eUrl = 'SuggestedForYouPaged'
    else if (idSection === 'favorites') eUrl = 'Favoritos'
    else if (idSection === 'seeAgain') eUrl = 'ver-novamente'

    const requestOptions = {
      ...options,
      signal: controller.signal
    }

    return api
      .get<SecaoActivesItemsResponse[]>(eUrl, dataParams, requestOptions)
      .then(res => {
        const data: VisibleSectionItem[] = []

        res?.data?.forEach(item => {
          const tp = ({
            midia: 'media',
            curso: 'course',
            formacao: 'trail'
          } as const)[item.tipo?.toLocaleLowerCase()]

          const mt = item.tipoMidia ? ({
            video: 'video',
            audio: 'audio',
            imagem: 'image',
            scorm: 'scorm',
            documento: 'document',
            link: 'link',
            pdf: 'pdf',
            carrossel: 'carousel',
            conteudolivre: 'freeContent'
          } as const)[item.tipoMidia.toLocaleLowerCase()] : null

          const entry = {
            id: item.id,
            title: item.titulo,
            type: tp,
            mediaType: mt || null,
            description: item.descricao,
            images: {
              horizontal: item.imagemDoCardHorizontal,
              vertical: item.imagemDoCardVertical
            },
            progress: item.porcentagemDeConclusao,
            favorite: item.favorito,
            reaction: item.reacao,
            totalReactions: item.totalReacao,
            enrolledAt: item.dataInscricao ? new Date(item.dataInscricao) : null,
            expired: item.expirado,
            daysToExpiration: item.diasParaExpirar,
            mandatory: item.obrigatorio,
            workload: item.cargaHoraria,
            createdAt: item.createdAt,
            parameters: null,
            competitions: item.competitions || []
          } as VisibleSectionItem

          if (typeof item.parametrizacaoDeConteudo !== 'undefined') {
            const ct = item.parametrizacaoDeConteudo

            entry.parameters = {
              comsumptionControl: {
                mode: ct.controleDeConsumo?.modoLiberacao,
                dateTime: ct.controleDeConsumo?.dataHoraLiberacao
              },
              externalDependencies: {
                courseIds: ct.dependenciasExternas?.cursoIds || [],
                courseDetails: Array.isArray(ct.dependenciasExternas?.cursoDetalhes)
                  ? ct.dependenciasExternas.cursoDetalhes.map(cd => ({
                    id: cd.id,
                    title: cd.titulo,
                    percentageOfProgress: cd.percentualDeProgresso,
                  }))
                  : [],
                trailIds: ct.dependenciasExternas.formacaoIds || [],
                trailDetails: Array.isArray(ct.dependenciasExternas?.trilhaDetalhes)
                  ? ct.dependenciasExternas.trilhaDetalhes.map(cd => ({
                    id: cd.id,
                    title: cd.titulo,
                    percentageOfProgress: cd.percentualDeProgresso,
                  }))
                  : [],
              }
            } as VisibleSectionItem['parameters']
          }

          data.push(entry)
        })

        return {
          success: true,
          page: res?.pageNumber,
          size: res?.pageSize,
          items: res?.totalItems,
          nPages: res?.totalPages,
          data
        }
      })
      .finally(() => {
        // Limpa o controlador
        this.activeSearchRequests.delete(idSection)
      })
  },

  async getActivesItemsPaged(idSection: string, filters: Object): Promise<ActivesItemsSectionsType> {
    let eUrl = `${idSection}/ActivesItemsPaged`
    if (idSection === 'continue') eUrl = 'KeepLearningPaged'
    else if (idSection === 'suggested') eUrl = 'SuggestedForYouPaged'

    try {
      return await api.get(eUrl, this.prepareParams(filters))
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao buscar itens da seção!' })
    }
  },

  async getCountActivesItems(params: FiltersParamsType, options?: any): Promise<{ data: { aprendizado: number; colecao: number }; success: boolean }> {
    try {
      return await api.get(`CountActivesItems`, { ...params }, options)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar carregar quantidade de itens!' })
    }
  },
  async associationContracts(sectionId: string, contracstIds: string[]): Promise<{ data: any[]; success: boolean }> {
    try {
      return await api.put(`${sectionId}/UpdateContratosSecoes`, contracstIds)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({ message: notifications[0] || 'Erro ao tentar vincular contratos a seção!' })
    }
  },

  async getSections(opt?: {
    search?: string
    //
    id?: string
    name?: string
    active?: boolean
    required?: boolean
    status?: string
    origin?: string
    public?: boolean
    //
    types?: string
    typeContents?: string[]
    inHome?: boolean
    //
    courseIds?: string[]
    sectionsIds?: string[]
    competenceIds?: string[]
    categoryIds?: string[]
    //
    page?: number
    size?: number
  }, options?: any) {
    const paramsDictionary = {
      search: 'search',
      //
      id: 'id',
      name: 'nome',
      active: 'ativo',
      required: 'obrigatorio',
      status: 'status',
      origin: 'origem',
      //
      types: 'tipo',
      typeContents: 'tiposConteudos',
      inHome: 'exibirNoAprendizado',
      //
      courseIds: 'cursoId',
      sectionsIds: 'secoesIds',
      competenceIds: 'competenciasIds',
      categoryIds: 'categoriasIds',
      //
      page: 'page',
      size: 'size'
    }

    const typeContentsDictionary = {
      trail: "formacao",
      course: "curso",
      video: "video",
      podcast: "podcast",
      image: "imagem",
      document: "documento",
      pdf: "pdf",
      scorm: "cursointerativo",
      link: "link"
    }

    const paramsToSend = {}

    if (opt) {
      Object.keys(opt).forEach((key) => {
        if (typeof paramsDictionary[key] !== 'undefined' && typeof opt[key] !== 'undefined' && opt[key] !== null)
          paramsToSend[paramsDictionary[key]] = opt[key]

        if (key === 'typeContents' && typeof paramsToSend[paramsDictionary[key]] === 'object') {
          paramsToSend[paramsDictionary[key]] = Object.values(opt[key]).map((item) => typeContentsDictionary[item] || item)
        }
      })
    }

    return api.get<any>('', paramsToSend, options)
      .then((res) => {
        let data = res.data?.map((item) => ({
          id: item.id || null,
          name: item.nome,
          observation: item.observacao,
          type: item.tipo,
          inHome: item.exibirNoAprendizado,
          redirectionLink: item.linkDeRedirecionamento,
          imageLink: item.linkDaImagem,
          initialPublicationDate: item.dtPublicacaoInicial,
          origin: item.origem,
          finalPublicationDate: item.dtPublicacaoFinal,
          createdAt: item.createdAt,
          orderHome: item.ordemAprendizado,
          orderCatalog: item.ordemCatalogo,
          roleIds: item.cargosIds,
          segmentationIds: item.segmentacoesIds,
          businessUnitIds: item.unidadesDeNegociosIds,
          departmentIds: item.departamentosIds,
          sectionItems: item.itensDaSecao,
          ativo: item.ativo,
          active: item.active,
          public: item.publico,
          contractIds: item.contratosIds,
          secaoOrigemId: item.secaoOrigemId,
        })) || []

        if (opt.public !== undefined && opt.public !== null) data = data.filter((item) => item.public === opt.public)

        return {
          status: res.success,
          page: res.pageNumber,
          size: res.pageSize,
          total: res.totalItems,
          pages: res.totalPages,
          data: data
        }
      })
  },

  async getFavorites() {
    return api.get<any>(`Favoritos`)
      .then((res) => {
        return {
          success: res.success,
          page: res.pageNumber,
          size: res.pageSize,
          totalItems: res.totalItems,
          pages: res.totalPages,
          data: res.data?.map((item) => ({
            id: item.id || null,
            tipo: item.tipo,
            tipoMidia: item.tipoMidia,
            titulo: item.titulo,
            sinopse: item.sinopse,
            descricao: item.descricao,
            likes: item.likes,
            liked: item.liked,
            favorito: item.favorito,
            inscrito: item.inscrito,
            porcentagemDeConclusao: item.porcentagemDeConclusao,
            urlDoRecurso: item.urlDoRecurso,
            urlDaImagem: item.urlDaImagem,
            competenciasIds: item.competenciasIds,
            expirado: item.expirado,
            scormScriptLink: item.scormScriptLink,
            obrigatorio: item.obrigatorio,
            categoriaId: item.categoriaId,
            origem: item.origem,
            dataExpirada: item.dataExpirada,
            secaoNome: item.secaoNome,
          }))
        }
      }).catch(() => {
        return {
          success: false,
          page: 0,
          size: 0,
          totalItems: 0,
          pages: 0,
          data: []
        }
      })
  },

  itemDetail({ type, id }: { type: 'course' | 'trail' | 'media', id: string }) {
    const eType = {
      course: 'curso',
      trail: 'trilha',
      media: 'midia'
    }[type]

    return api.get<any>(`item/${eType}/detalhes/${id}`)
      .then(async (res) => {
        // Solução temporária para o caso de carrossel
        if (res?.success && res?.data && res.data.tipo?.toLowerCase() === 'carrossel') {
          const media = await midiaService.getFile(id)

          if (media?.success && media?.data) {
            res.data.carousel = media.data.carousel?.map(el => el.url) || []
          }
        }

        return {
          success: res.success,
          data: res.data || {}
        }
      })
      .catch((e) => { throw new Error(e) })
  },

  imageUpload(id: string, file, type: 'card' | 'banner') {
    const newType = type === 'card' ? 'Card' : 'Banner'
    const formData = new FormData()
    formData.append('arquivo', file)

    return api.post(`${id}/FileUpload/CardAndBanner/${newType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  getTop10(
    criteria: 'Inscricoes' | 'Reacoes' | 'Conclusoes' | 'Manual',
    range: 'Semanal' | 'Mensal' | 'Total'
  ) {
    return api.get<TopLancamentosItemResponse[]>(['top10', criteria, range, 'itens'].join('/'))
  },

  getReleases(
    criteria: 'Inscricoes' | 'Reacoes' | 'Conclusoes' | 'DataCriacao' | 'Manual',
    range: 'Semanal' | 'Mensal' | 'Total'
  ) {
    return api.get<TopLancamentosItemResponse[]>(['lancamentos', criteria, range, 'itens'].join('/'))
  }
}

export default api
